import { EventBus, type Unsubscribe } from '../events/event_bus';
import type {
  FromServerMsgV2 as FromServerMsg,
  SessionBootstrapMsg,
  SdpOfferMsg,
  SessionConflictMsg,
  DeviceOfflineMsg,
  QualityAdaptedAckMsg,
  CameraQosMsg,
  MetricsPushMsg,
  QosReportMsg,
  SessionTakeoverResponseMsg,
} from '../protocol/signaling';
import { readAudioCapability } from '../protocol/signaling';
import { sessionTransition } from './state_machine';
import type { SignalingClient } from '../signaling/signaling_client';
import { InteractiveTransportSession } from '../transport/interactive/interactive_transport';
import { QosController } from '../qos/qos_controller';
import type {
  AudioCapabilityState,
  CodecType,
  RenderSurfaceTarget,
  SdkEvent,
  SessionDebugSnapshot,
  TransportFamily,
  VgpsLiveSession,
  VgpsPlaybackSession,
  VgpsSessionState,
} from '../runtime/types';
import type { SessionState } from './state_machine';
import { AbrController } from '../qos/abr_controller';
import type { AbrAction } from '../qos/abr_controller';
import { StreamSocketTransport } from '../transport/stream_socket/stream_socket_transport';

const QOS_POLL_INTERVAL_MS = 3000;

function emitLiveTiming(stage: string, sessionId: string): void {
  if (typeof window === 'undefined') return;
  const detail = { stage, sessionId, atMs: Date.now() };
  window.dispatchEvent(new CustomEvent('vgps-live-timing', { detail }));
}

// Map state_machine.SessionState to the public VgpsSessionState
function toPublicState(s: SessionState): VgpsSessionState {
  if (s === 'opening') return 'connecting';
  if (s === 'transport_connecting') return 'connecting';
  return s as VgpsSessionState;
}

/**
 * Probe whether the current browser advertises an H.265/HEVC RTP decoder for
 * WebRTC. Returns true on environments where capability cannot be detected
 * (e.g. server-side, older browsers without `getCapabilities`) so that
 * negotiation is allowed to proceed and any failure surfaces through the
 * normal ICE/transport error path.
 *
 * As of 2026-04 Firefox (≤148) does not ship an HEVC RTP decoder; Chrome/Edge
 * 107+ on supported platforms do.
 */
function browserSupportsWebRtcH265(): boolean {
  try {
    const RtpReceiver = (globalThis as { RTCRtpReceiver?: typeof RTCRtpReceiver }).RTCRtpReceiver;
    const caps = RtpReceiver?.getCapabilities?.('video');
    if (!caps?.codecs) return true;
    return caps.codecs.some((c) => /H265|HEVC/i.test(c.mimeType ?? ''));
  } catch {
    return true;
  }
}

interface SessionInit {
  sessionId: string;
  deviceSerial: string;
  channelId: string;
  qualityHint?: 'uhd' | 'hd' | 'sd' | 'auto';
  mode: 'live' | 'playback';
  startTsMs?: number;
  globalEvents: EventBus<SdkEvent>;
  signaling: SignalingClient;
  onClose: (sessionId: string) => void;
  streamSocketFactory?: () => StreamSocketTransport;
  relayHostOverride?: string;
}

class BaseSession {
  readonly sessionId: string;
  readonly deviceSerial: string;
  readonly channelId: string;
  readonly mode: 'live' | 'playback';

  private machineState: SessionState = 'idle';
  private machineAttempts = 0;
  private _transportFamily: TransportFamily = 'stream_socket';
  private _codec: CodecType = 'h264';

  private readonly stateListeners = new Set<(state: VgpsSessionState) => void>();
  private readonly globalEvents: EventBus<SdkEvent>;
  protected readonly signaling: SignalingClient;
  private readonly onClose: (id: string) => void;
  private readonly qualityHint?: 'uhd' | 'hd' | 'sd' | 'auto';

  private transport = new InteractiveTransportSession();
  private streamSocketTransport: StreamSocketTransport | null = null;
  /**
   * Last audio capability seen for this session's effective channel.
   *
   * Starts 'unknown' and is only ever moved off 'unknown' by a bootstrap that
   * actually carries the key. Deliberately re-derived per bootstrap rather than
   * remembered across sessions: caching an absent answer as a negative is the
   * failure mode where a tile stays silent forever once the device starts
   * reporting.
   */
  private _audioCapability: AudioCapabilityState = 'unknown';
  private readonly streamSocketFactory: (() => StreamSocketTransport) | undefined;
  private readonly relayHostOverride: string | undefined;
  private readonly qos = new QosController();
  private readonly abr = new AbrController(this._transportFamily);
  private qosPollTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** JT/T1078 lease keepalive — periodic `request` message so firmware doesn't
   *  idle-evict MDVR_JTT1078_IDLE_LEASE_MS. Only active on stream_socket. */
  private viewerRequestKeepaliveTimer: ReturnType<typeof setInterval> | null = null;
  /** Rate-limit for viewer-requests (keepalive + recovery share it; each forces
   *  a firmware IDR, so keep them >=2 s apart to avoid an IDR storm). */
  private lastViewerRequestAtMs = 0;
  private lastQuality: 'good' | 'degraded' | 'poor' | null = null;
  private lastEncoderTelemetry: Record<string, unknown> = {};

  private renderSurface: RenderSurfaceTarget | null = null;
  private pendingStream: MediaStream | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private transportConnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(init: SessionInit) {
    this.sessionId = init.sessionId;
    this.deviceSerial = init.deviceSerial;
    this.channelId = init.channelId;
    this.mode = init.mode;
    this.qualityHint = init.qualityHint;
    this.globalEvents = init.globalEvents;
    this.signaling = init.signaling;
    this.onClose = init.onClose;
    this.streamSocketFactory = init.streamSocketFactory;
    this.relayHostOverride = init.relayHostOverride;

    this.signaling.registerSession(this.sessionId, this.handleSignalingMessage);
    this.dispatch({ type: 'OPEN' });
  }

  get state(): VgpsSessionState {
    return toPublicState(this.machineState);
  }

  get transportFamily(): TransportFamily {
    return this._transportFamily;
  }

  get codec(): CodecType {
    return this._codec;
  }

  /** Audio capability for this session's effective channel, from the last
   *  `session_bootstrap`. 'unknown' means unproven, not negative. */
  get audioCapability(): AudioCapabilityState {
    return this._audioCapability;
  }

  /**
   * Make this session the page's one audible stream, or silence it.
   * Must be called synchronously from the click handler — see `VgpsSession`.
   */
  setAudioEnabled(enabled: boolean): void {
    this.streamSocketTransport?.setAudioEnabled(enabled);
  }

  setAudioVolume(volume: number): void {
    this.streamSocketTransport?.setAudioVolume(volume);
  }

  subscribeState(listener: (state: VgpsSessionState) => void): Unsubscribe {
    this.stateListeners.add(listener);
    listener(toPublicState(this.machineState));
    return () => { this.stateListeners.delete(listener); };
  }

  async attachRenderSurface(target: RenderSurfaceTarget): Promise<void> {
    console.log(`[Session:${this.sessionId.slice(-6)}] attachRenderSurface ch=${this.channelId} state=${this.machineState} hasPendingStream=${Boolean(this.pendingStream)}`);
    this.renderSurface = target;
    const v = target.videoElement;
    v.muted = target.muted ?? true;
    v.playsInline = target.playsInline ?? true;
    v.autoplay = true;
    v.controls = false;
    v.style.backgroundColor = '#101820';
    v.setAttribute('data-vgps-session', this.sessionId);

    this.transport.onTrack((stream) => {
      console.log(`[Session:${this.sessionId.slice(-6)}] transport.onTrack (interactive) ch=${this.channelId}`);
      this.pendingStream = stream;
      this.applyStreamToSurface(stream, target);
    });

    if (this.pendingStream) {
      console.log(`[Session:${this.sessionId.slice(-6)}] applying existing pendingStream on attach ch=${this.channelId}`);
      this.applyStreamToSurface(this.pendingStream, target);
    }
  }

  async detachRenderSurface(): Promise<void> {
    console.log(`[Session:${this.sessionId.slice(-6)}] detachRenderSurface ch=${this.channelId}`);
    if (!this.renderSurface) return;
    const v = this.renderSurface.videoElement;
    v.srcObject = null;
    v.removeAttribute('data-vgps-session');
    this.renderSurface = null;
  }

  async close(): Promise<void> {
    this.dispatch({ type: 'CLOSE' });
  }

  async debugSnapshot(): Promise<SessionDebugSnapshot> {
    // In stream_socket mode the interactive transport is idle — use the
    // StreamSocketTransport metrics so the Stats panel gets real values.
    const metrics: Record<string, unknown> = this.streamSocketTransport
      ? this.streamSocketTransport.getMetrics()
      : await this.transport.getMetrics().catch(() => ({}));
    return {
      sessionId: this.sessionId,
      deviceSerial: this.deviceSerial,
      channelId: this.channelId,
      state: toPublicState(this.machineState),
      transportFamily: this._transportFamily,
      codec: this._codec,
      message: JSON.stringify({ ...metrics, ...this.lastEncoderTelemetry }),
    };
  }

  // ── QoS polling ───────────────────────────────────────────────────────────

  private startQosPoll(): void {
    if (this.qosPollTimer !== null) return;
    this.qosPollTimer = setInterval(() => { this.pollQos().catch(() => { /* interval will retry */ }); }, QOS_POLL_INTERVAL_MS);
  }

  private stopQosPoll(): void {
    if (this.qosPollTimer === null) return;
    clearInterval(this.qosPollTimer);
    this.qosPollTimer = null;
    this.lastQuality = null;
  }

  // ── Session heartbeat (Phase B lease keepalive) ─────────────────

  /** Start sending session_heartbeat every 15 s to keep the interactive lock alive. */
  private startHeartbeat(): void {
    if (this.heartbeatTimer !== null) return;
    this.heartbeatTimer = setInterval(() => {
      try {
        this.signaling.send({
          type: 'session_heartbeat',
          session_id: this.sessionId,
          device_serial: this.deviceSerial,
          channel_id: this.channelId,
        });
      } catch { /* WebSocket closed — will reconnect */ }
    }, 15_000);
  }

  /**
   * Send `{type:'request', id, channel}` over signaling so firmware re-pushes
   * its cached IDR (cheap, no encoder churn) and forces a fresh IDR. Used by
   * both the recovery flow (FrameQualityGate flipped to 'recovering') and the
   * lease keepalive timer. Rate-limited to 1/2s.
   */
  private sendViewerRequest(_reason: 'recovery' | 'keepalive'): void {
    const now = Date.now();
    // Single 2 s limiter for BOTH keepalive and recovery. The firmware forces a
    // fresh encoder IDR on EVERY `request` (jtt1078_agent: request_live_idr), so
    // a tighter recovery cadence (the previous 500 ms) collapsed the GOP to ~0.5 s
    // — an IDR storm that, combined with the backward-PTS keyframe anomaly, fed a
    // self-sustaining recovering loop. Keep IDR-forcing requests >=2 s apart.
    if (now - this.lastViewerRequestAtMs < 2000) return;
    this.lastViewerRequestAtMs = now;
    try {
      this.signaling.send({
        type: 'request',
        id: this.sessionId,
        channel: this.channelId,
      } as unknown as Parameters<typeof this.signaling.send>[0]);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Session] sendViewerRequest failed:', err);
    }
  }

  private stopViewerRequestKeepalive(): void {
    if (this.viewerRequestKeepaliveTimer !== null) {
      clearInterval(this.viewerRequestKeepaliveTimer);
      this.viewerRequestKeepaliveTimer = null;
    }
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer === null) return;
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private async pollQos(): Promise<void> {
    // Only report QoS when there is an active media stream.
    // In transport_connecting/opening/reconnecting the RTCPeerConnection exists but
    // has no video track — getMetrics() returns {} (truthy) which classifies as
    // 'good', producing phantom qos_reports that trigger spurious ABR quality_hints.
    if (this.machineState !== 'playing' && this.machineState !== 'buffering') return;
    const metrics = await this.transport.getMetrics().catch(() => null);
    if (!metrics) return;

    // Guard 2: no real transport data yet (RTCPeerConnection exists but no inbound-rtp).
    // This happens when the streamer is killed but ICE has not timed out (~30 s), or
    // briefly at session start before the first stats report arrives.
    // getMetrics() returns {} in these cases — sending state=good with only a timestamp
    // is misleading and can trigger spurious ABR quality_hints on the server.
    const hasRealMetrics =
      metrics.rttMs !== undefined ||
      metrics.jitterMs !== undefined ||
      metrics.framesPerSecond !== undefined ||
      metrics.packetLossRatio !== undefined;
    if (!hasRealMetrics) return;

    // Classify using context from AbrController (has real targetBitrate + smoothedThroughput)
    const ctx = this.abr.getClassifyContext();
    const quality = this.qos.classify(metrics, ctx);

    // Advance ABR state machine; get intent action (advisory only — server decides)
    const abrAction: AbrAction | undefined = this.abr.tick(quality, metrics);

    // Phase C: Send qos_report uplink to signaling server.
    // Include abr_hint as advisory — server is the final ABR authority.
    // For JT/T1078 multi-viewer, server aggregates all viewer reports before acting.
    const qosReport: QosReportMsg = {
      type: 'qos_report',
      session_id: this.sessionId,
      device_serial: this.deviceSerial,
      channel_id: this.channelId,
      family: this._transportFamily,
      state: quality,
      metrics: {
        packet_loss_ratio: metrics.packetLossRatio,
        rtt_ms: metrics.rttMs,
        frames_per_second: metrics.framesPerSecond,
        jitter_ms: metrics.jitterMs,
        available_incoming_bitrate_kbps: metrics.availableIncomingBitrateKbps,
        encoder_qp: metrics.encoderQp,
        actual_fps: metrics.actualFps,
        applied_profile: metrics.appliedProfile,
        timestamp_ms: metrics.timestampMs ?? Date.now(),
      },
      abr_hint: abrAction ? {
        action: abrAction.action,
        rendition: abrAction.rendition,
        bitrate_kbps: abrAction.bitrateKbps,
        fps: abrAction.fps,
        tier: abrAction.tier,
        reason: abrAction.reason,
        level: abrAction.level as 1 | 2 | 3,
      } : undefined,
    };
    try {
      this.signaling.send(qosReport);
    } catch { /* WebSocket closed — will reconnect */ }

    // Emit network_state on first measurement or whenever quality changes
    if (quality !== this.lastQuality) {
      this.lastQuality = quality;
      try {
        this.globalEvents.emit({
          type: 'network_state',
          sessionId: this.sessionId,
          state: quality,
          metrics,
        });
      } catch { /* subscriber threw — isolate */ }
    }
  }

  // ── State machine dispatcher ──────────────────────────────────────────────

  protected dispatch(event: Parameters<typeof sessionTransition>[1]): void {
    const prev = this.machineState;
    const result = sessionTransition(this.machineState, event, this.machineAttempts);
    this.machineState = result.next;
    this.machineAttempts = result.nextAttempts;
    if (prev !== result.next) {
      console.log(`[Session:${this.sessionId.slice(-6)}] state ${prev} → ${result.next} (event=${event.type}) ch=${this.channelId}`);
    }
    this.executeEffects(result.effects);
  }

  private executeEffects(effects: ReturnType<typeof sessionTransition>['effects']): void {
    for (const effect of effects) {
      switch (effect.type) {
        case 'SEND_OPEN':
          emitLiveTiming('session_open_sent', this.sessionId);
          this.signaling.send({
            type: 'session_open',
            request_id: `req-${this.sessionId}`,
            session_id: this.sessionId,
            target_device_serial: this.deviceSerial,
            target_channel_id: this.channelId,
            mode: this.mode,
            // Declare transport preference so server enforces correct locking policy.
            // interactive = single-viewer lock; stream_socket = multi-viewer, no lock.
            preferred_family: this._transportFamily,
            quality_hint: this.qualityHint,
          });
          break;

        case 'START_TRANSPORT':
          if (this.machineState === 'transport_connecting') {
            this.startTransportConnectTimeout();
          } else {
            this.clearTransportConnectTimeout();
            this.startQosPoll();
          }
          break;

        case 'STOP_TRANSPORT':
          this.stopQosPoll();
          this.clearTransportConnectTimeout();
          this.stopHeartbeat();
          this.stopViewerRequestKeepalive();
          this.transport.close();
          this.streamSocketTransport?.close();
          this.streamSocketTransport = null;
          this.clearReconnectTimer();
          // Deliberately still sent on the retry path: the server-visible
          // traffic of a reconnect is unchanged by the CLOSE_SESSION split
          // (session_stop, then session_open from SEND_OPEN). Only this
          // client's own bookkeeping moved.
          this.signaling.send({ type: 'session_stop', session_id: this.sessionId });
          break;

        case 'CLOSE_SESSION':
          // Terminal only. Stop routing signaling to this session and tell the
          // runtime it is gone. Doing this on the retry path unregistered the
          // session before its own reconnect could be answered — see
          // `scripts/verify-session-reconnect.mjs`.
          this.signaling.unregisterSession(this.sessionId);
          this.onClose(this.sessionId);
          break;

        case 'EMIT_STATE': {
          const pub = toPublicState(effect.state);
          for (const l of this.stateListeners) l(pub);
          this.globalEvents.emit({
            type: 'player',
            sessionId: this.sessionId,
            state: pub,
            message: effect.message,
          });
          break;
        }

        case 'EMIT_TRANSPORT_SWITCHED':
          this._transportFamily = effect.family;
          this.globalEvents.emit({
            type: 'transport_switched',
            sessionId: this.sessionId,
            family: effect.family,
          });
          break;

        case 'SCHEDULE_RECONNECT':
          this.clearReconnectTimer();
          this.reconnectTimer = setTimeout(() => {
            this.dispatch({ type: 'RECONNECT_TICK' });
          }, effect.delayMs);
          break;
      }
    }
  }

  private startTransportConnectTimeout(): void {
    this.clearTransportConnectTimeout();
    this.transportConnectTimer = setTimeout(() => {
      if (this.machineState === 'transport_connecting') {
        this.dispatch({ type: 'TRANSPORT_FAILED', reason: 'media_timeout', canRetry: false });
      }
    }, 12_000);
  }

  private clearTransportConnectTimeout(): void {
    if (this.transportConnectTimer !== null) {
      clearTimeout(this.transportConnectTimer);
      this.transportConnectTimer = null;
    }
  }

  // ── Signaling message handler ─────────────────────────────────────────────

  private handleSignalingMessage = (msg: FromServerMsg): void => {
    if (msg.type === 'session_bootstrap') {
      this.handleBootstrap(msg).catch((err) => {
        this.dispatch({ type: 'TRANSPORT_FAILED', reason: err instanceof Error ? err.message : 'bootstrap_error', canRetry: true });
      });
    } else if (msg.type === 'sdp_offer') {
      this.handleOffer(msg).catch((err) => {
        this.dispatch({ type: 'TRANSPORT_FAILED', reason: err instanceof Error ? err.message : 'offer_error', canRetry: true });
      });
    } else if (msg.type === 'device_offline') {
      this.handleDeviceOffline(msg);
    } else if (msg.type === 'session_error') {
      this.handleSessionError(msg);
    } else if (msg.type === 'session_conflict') {
      this.handleSessionConflict(msg);
    } else if (msg.type === 'quality_adapted_ack') {
      this.handleQualityAdaptedAck(msg);
    } else if (msg.type === 'camera_qos') {
      this.handleCameraQos(msg);
    } else if (msg.type === 'metrics_push') {
      this.handleMetricsPush(msg);
    } else if (msg.type === 'capability_report') {
      this.handleCapabilityReport(msg);
    }
  };

  /**
   * Read the per-channel audio capability out of a bootstrap and publish it.
   *
   * Three states, decided by a presence test — never truthiness. ABSENT is
   * unproven, not negative, so it leaves the stored value at 'unknown' and the
   * host is expected to enable the control optimistically and let the wire
   * decide. It is deliberately NOT remembered across sessions.
   *
   * `msg.target.channel_id` is the effective channel (101/102/201/202), which
   * is what the answer is keyed on — an HD↔SD toggle opens a new session on a
   * different channel and re-asks.
   */
  private handleAudioCapability(msg: SessionBootstrapMsg): void {
    const audio = readAudioCapability(msg);
    if (audio === undefined) {
      this._audioCapability = 'unknown';
    } else {
      this._audioCapability = audio.available === true ? 'available' : 'unavailable';
    }

    const channelId = msg.target?.channel_id ?? this.channelId;
    this.globalEvents.emit({
      type: 'audio_capability',
      sessionId: this.sessionId,
      deviceSerial: this.deviceSerial,
      channelId,
      capability: this._audioCapability,
      codec: audio?.codec,
      sampleRate: audio?.sample_rate,
      channels: audio?.channels,
    });
  }

  private async handleBootstrap(msg: SessionBootstrapMsg): Promise<void> {
    try {
      emitLiveTiming('session_bootstrap_received', this.sessionId);
      this.handleAudioCapability(msg);
      const interactive = msg.transports.find((t) => t.family === 'interactive');
      const ssDesc = msg.transports.find((t) => t.family === 'stream_socket');

      // Dispatch bootstrap event. P2P and stream_socket are equal-first options —
      // the signaling server determines which transport(s) the device supports and
      // includes only those in the bootstrap. They do not race.
      this.dispatch({
        type: 'BOOTSTRAP_RECEIVED',
        hasInteractive: Boolean(interactive),
        hasStreamSocket: Boolean(ssDesc),
      });

      if (interactive) {
        // P2P-mode: set up WebRTC peer connection.
        this.transport.init(interactive.network_access.ice_servers);

        this.transport.onTrack((stream) => {
          this.pendingStream = stream;
          if (this.renderSurface) this.applyStreamToSurface(stream, this.renderSurface);
        });

        // 'failed'       = ICE definitively broken → schedule reconnect.
        // 'disconnected' = ICE transient loss → schedule reconnect with grace delay.
        this.transport.onConnectionFailed((state) => {
          this.dispatch({
            type: 'TRANSPORT_FAILED',
            reason: `peer_connection_${state}`,
            canRetry: true,
          });
        });

        this.signaling.send({
          type: 'session_start',
          session_id: this.sessionId,
          device_serial: this.deviceSerial,
          channel_id: this.channelId,
          mode: this.mode,
          protocol: 'p2p',
        });
        emitLiveTiming('session_start_sent', this.sessionId);
        this.startHeartbeat();
      }

      if (ssDesc && this.streamSocketFactory) {
        console.log(`[Session:${this.sessionId.slice(-6)}] bootstrap: stream_socket ch=${this.channelId} uri=${ssDesc.network_access.connect_endpoints[0]?.uri}`);
        // stream_socket mode: connect directly to JTT1078 relay WebSocket.
        // When bootstrap has only stream_socket (no interactive), this is the
        // sole transport path — no race with P2P.

        // Notify the streamer to start pushing frames to the relay.
        // In stream_socket-only mode session_start is never sent by the interactive
        // branch above, so we send it here with protocol='stream_socket'.
        // Without this the streamer never calls startStream() and the relay has
        // nothing to push to the viewer.
        this.signaling.send({
          type: 'session_start',
          session_id: this.sessionId,
          device_serial: this.deviceSerial,
          channel_id: this.channelId,
          mode: this.mode,
          protocol: 'stream_socket',
        });
        emitLiveTiming('session_start_sent', this.sessionId);

        let uri = ssDesc.network_access.connect_endpoints[0]?.uri;
        if (uri && this.relayHostOverride) {
          // Rewrite the relay host when the signaling server advertises an
          // internal address (e.g. 127.0.0.1) that is not reachable from the browser.
          try {
            const parsed = new URL(uri);
            const original = parsed.host;
            parsed.hostname = this.relayHostOverride;
            uri = parsed.toString();
            console.log(`[Session] relay URI host rewritten: ${original} → ${this.relayHostOverride}  (${uri})`);
          } catch {
            console.warn('[Session] relayHostOverride: failed to parse relay URI, using as-is:', uri);
          }
        }
        if (uri) {
          const sst = this.streamSocketFactory();
          this.streamSocketTransport = sst;
          sst.onTrack((stream) => {
            console.log(`[Session:${this.sessionId.slice(-6)}] sst.onTrack ch=${this.channelId} state=${this.machineState} hasRenderSurface=${Boolean(this.renderSurface)}`);
            if (this.machineState !== 'playing') {
              this.pendingStream = stream;
              if (this.renderSurface) this.applyStreamToSurface(stream, this.renderSurface);
              this.dispatch({ type: 'TRANSPORT_CONNECTED', family: 'stream_socket' });
            }
          });
          sst.onError((err) => {
            this.dispatch({
              type: 'TRANSPORT_FAILED',
              reason: err.message,
              canRetry: !(err as Error & { nonRetryable?: boolean }).nonRetryable,
            });
          });
          sst.onFrameQuality((change) => {
            this.globalEvents.emit({
              type: 'frame_quality',
              sessionId: this.sessionId,
              state: change.state,
              reason: change.reason,
              droppedFrames: change.droppedFrames,
            });
            // No recovery `request` on 'recovering': each request forces a
            // firmware IDR; firing them per-recovery collapsed the GOP into an
            // IDR storm. The delay buffer + natural keyframes handle loss.
          });
          sst.onAudioState((change) => {
            this.globalEvents.emit({
              type: 'audio_state',
              sessionId: this.sessionId,
              enabled: change.enabled,
              contextRunning: change.contextRunning,
              sampleRate: change.sampleRate,
              codec: change.codec,
              receivingAudio: change.receivingAudio,
              unavailableReason: change.unavailableReason ?? null,
            });
          });
          sst.connect(uri);
          // Lease keepalive (firmware idle-evicts at MDVR_JTT1078_IDLE_LEASE_MS).
          this.sendViewerRequest('keepalive');
          this.viewerRequestKeepaliveTimer = setInterval(() => {
            this.sendViewerRequest('keepalive');
          }, 20_000);
        }
      }
    } catch (err) {
      this.dispatch({
        type: 'TRANSPORT_FAILED',
        reason: err instanceof Error ? err.message : 'bootstrap_failed',
        canRetry: true,
      });
    }
  }

  private async handleOffer(msg: SdpOfferMsg): Promise<void> {
    try {
      // Detect codec from SDP offer (a=rtpmap line) before answering.
      // H265/HEVC may appear as "H265" or "HEVC" (RFC 7798 §7.1).
      this._codec = /a=rtpmap:\d+\s+(H265|HEVC)\//i.test(msg.sdp) ? 'h265' : 'h264';

      // Fail-fast: if the camera offers H.265 over WebRTC but this browser does
      // not advertise an HEVC RTP decoder (e.g. Firefox as of v148), the SDP
      // answer would reject the m-line and ICE never starts → silent black
      // screen. Surface a clear, customer-facing message instead so support
      // staff can manually reconfigure the device to use the JT/T1078 transport.
      if (this._codec === 'h265' && !browserSupportsWebRtcH265()) {
        this.dispatch({
          type: 'TRANSPORT_FAILED',
          reason:
            'Trình duyệt hiện tại không cho phép liveview H.265/HEVC. ' +
            'Vui lòng sử dụng Google Chrome hoặc Microsoft Edge phiên bản mới, ' +
            'hoặc liên hệ VGPS để được hỗ trợ.',
          canRetry: false,
        });
        return;
      }

      const answerSdp = await this.transport.handleOffer(msg.sdp);
      this.signaling.send({
        type: 'sdp_answer',
        session_id: this.sessionId,
        sdp: answerSdp,
      });
      this.dispatch({ type: 'TRANSPORT_CONNECTED', family: 'interactive' });
    } catch (err) {
      this.dispatch({
        type: 'TRANSPORT_FAILED',
        reason: err instanceof Error ? err.message : 'offer_handling_failed',
        canRetry: true,
      });
    }
  }

  // ── Conflict & quality adaptation handlers ───────────────────────────────

  /**
   * Phase B: Handle session_conflict from signaling server.
   * Server detected an existing interactive lock on this device+channel.
   * Emit SdkEvent so the UI can prompt the user for takeover decision.
   * Player must call sendTakeoverResponse() to proceed or abort.
   */
  private handleSessionConflict(msg: SessionConflictMsg): void {
    this.globalEvents.emit({
      type: 'session_conflict',
      sessionId: this.sessionId,
      deviceSerial: msg.device_serial,
      channelId: msg.channel_id,
      family: msg.family,
      role: msg.role,
      expiresAtMs: msg.expires_at_ms,
    });
  }

  /**
   * Handle device_offline from signaling server.
   * Additive, informational signal — the session state machine is intentionally
   * NOT transitioned (it stays whatever it is, typically 'connecting'), so a
   * camera that connects after session_open still recovers. The emitted SdkEvent
   * lets the player surface an immediate "camera offline" overlay; it is cleared
   * by the host when media starts flowing (state → 'playing').
   */
  private handleDeviceOffline(msg: DeviceOfflineMsg): void {
    this.globalEvents.emit({
      type: 'device_offline',
      sessionId: this.sessionId,
      deviceSerial: msg.device_serial,
      channelId: msg.channel_id,
      reason: msg.reason,
    });
  }

  private handleSessionError(msg: Extract<FromServerMsg, { type: 'session_error' }>): void {
    this.globalEvents.emit({
      type: 'session_error',
      sessionId: this.sessionId,
      code: msg.code,
      message: msg.message,
    });
  }

  /**
   * Phase B: Send takeover response to signaling server.
   * Called by VgpsLiveSession.respondToTakeover() after the user decides.
   * accept=true  → server revokes incumbent, grants this session.
   * accept=false → this session is abandoned, incumbent is unaffected.
   */
  sendTakeoverResponse(accept: boolean): void {
    const msg: SessionTakeoverResponseMsg = {
      type: 'session_takeover_response',
      session_id: this.sessionId,
      device_serial: this.deviceSerial,
      channel_id: this.channelId,
      accept,
    };
    this.signaling.send(msg);
  }

  /**
   * Handle capability_report from streamer (relayed via signaling server or initial bootstrap).
   * Updates AbrController and emits updated ClassifyContext for next poll.
   * Phase D: Server persists capability; SDK caches for session lifetime.
   */
  private handleCapabilityReport(msg: import('../protocol/signaling').CapabilityReportMsg): void {
    // 4-channel model: streamer sends channel_id (e.g. "102"); legacy sends camera_id.
    // Match channel_id first, then fall back to camera_id integer match, then first camera.
    const camera =
      msg.cameras.find(c => c.channel_id !== undefined && String(c.channel_id) === this.channelId) ??
      msg.cameras.find(c => String(c.camera_id) === this.channelId) ??
      msg.cameras[0];
    if (!camera) return;
    // Convert wire format to CameraCapability
    const cap: import('../runtime/types').CameraCapability = {
      cameraId: camera.camera_id,
      nominalFps: camera.nominal_fps,
      transports: (camera.transports ?? []).map((t: any) => ({
        family: t.family,
        protocol: t.protocol,
        enabled: t.enabled,
        priority: t.priority,
        defaultForLive: t.default_for_live,
      })),
      renditions: (camera.renditions ?? []).map((r: any) => ({
        label: r.label,
        width: r.width,
        height: r.height,
        fpsMin: r.fps_min,
        fpsMax: r.fps_max,
        bitrateMinKbps: r.bitrate_min_kbps,
        bitrateMaxKbps: r.bitrate_max_kbps,
        sharedFromRecording: r.shared_from_recording,
        recordingSafeMinBitrateKbps: r.recording_safe_min_bitrate_kbps,
        recordingSafeMinFps: r.recording_safe_min_fps,
      })),
      rcModes: camera.rc_modes ?? [],
      codecs: camera.codecs ?? [],
    };
    this.abr.updateCapability(cap);
  }

  /**
   * Handle quality_adapted_ack from streamer/firmware (relayed via signaling server).
   * Emits quality_adapted SdkEvent so player/app can update UI.
   * Phase D TODO: Use rendition info to update local ABR state / cooldown.
   */
  private handleQualityAdaptedAck(msg: QualityAdaptedAckMsg): void {
    const abrState = this.abr.getState();
    this.globalEvents.emit({
      type: 'quality_adapted',
      sessionId: this.sessionId,
      rendition: msg.rendition,
      bitrateKbps: msg.bitrate_kbps,
      fps: msg.fps,
      tier: msg.tier,
      reason: abrState.goodCount > 0 ? 'recovery' : 'congestion',
      level: abrState.level === 0 ? 1 : abrState.level as 1 | 2 | 3,
      hasDowntime: msg.rendition !== undefined && msg.rendition !== abrState.renditionLabel,
    });
  }

  /**
   * Handle camera_qos push from streamer (WebRTC path).
   * Merges encoder telemetry into a synthetic TransportMetrics snapshot for
   * classification. ts_ms is required for freshness checks.
   *
   * Phase D TODO: Compare with last applied_profile to detect convergence.
   */
  private handleCameraQos(msg: CameraQosMsg): void {
    const pushed = {
      encoderQp: msg.mean_qp,
      actualFps: msg.actual_fps,
      appliedProfile: msg.applied_profile,
      timestampMs: msg.ts_ms,
    };
    // Store for debugSnapshot() so stats panel can display encoder telemetry.
    this.lastEncoderTelemetry = pushed;
    // Re-classify with the pushed encoder state; emit network_state if changed.
    const quality = this.qos.classify(pushed, this.abr.getClassifyContext());
    if (quality !== this.lastQuality) {
      this.lastQuality = quality;
      this.globalEvents.emit({
        type: 'network_state',
        sessionId: this.sessionId,
        state: quality,
        metrics: pushed,
      });
    }
  }

  /**
   * Handle metrics_push from JT/T1078 relay/firmware (stream_socket path).
   * Carries 0x9105 packet-loss + applied-state telemetry.
   * ts_ms is required; stale metrics treated as degraded.
   *
   * Phase D TODO: Feed into server-side ABR aggregation (this is client-side notify path only).
   */
  private handleMetricsPush(msg: MetricsPushMsg): void {
    const pushed = {
      packetLossRatio: msg.packet_loss_rate !== undefined ? msg.packet_loss_rate / 100 : undefined,
      appliedProfile: msg.applied_profile,
      actualFps: msg.actual_fps,
      encoderQp: msg.encoder_qp,
      timestampMs: msg.ts_ms,
    };
    // Store for debugSnapshot() so stats panel can display encoder telemetry.
    this.lastEncoderTelemetry = pushed;
    // JT/T1078: hasRtt=false baked into AbrController context for stream_socket family
    const quality = this.qos.classify(pushed, this.abr.getClassifyContext());
    if (quality !== this.lastQuality) {
      this.lastQuality = quality;
      this.globalEvents.emit({
        type: 'network_state',
        sessionId: this.sessionId,
        state: quality,
        metrics: pushed,
      });
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private applyStreamToSurface(stream: MediaStream, target: RenderSurfaceTarget): void {
    console.log(`[Session:${this.sessionId.slice(-6)}] applyStreamToSurface ch=${this.channelId} tracks=${stream.getTracks().length}`);
    target.videoElement.srcObject = stream;
    void target.videoElement.play().catch((err) => {
      console.warn(`[Session:${this.sessionId.slice(-6)}] video.play() failed:`, err);
    });
  }

  protected emitPlaybackClock(mediaTsMs: number, anchorTsMs: number | undefined, rate: number, playing: boolean): void {
    this.globalEvents.emit({
      type: 'playback_clock',
      sessionId: this.sessionId,
      mediaTsMs,
      anchorTsMs,
      rate,
      playing,
    });
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

// ── LiveSession ───────────────────────────────────────────────────────────────

export class LiveSession extends BaseSession implements VgpsLiveSession {
  async requestSyncFrame(): Promise<void> {
    // Phase 1: no-op. Phase 2: send control message via DataChannel.
  }

  /**
   * Respond to a session_conflict takeover prompt.
   * Delegates to BaseSession.sendTakeoverResponse.
   */
  respondToTakeover(accept: boolean): void {
    this.sendTakeoverResponse(accept);
  }
}

// ── PlaybackSession ───────────────────────────────────────────────────────────

export class PlaybackSession extends BaseSession implements VgpsPlaybackSession {
  private playbackRate = 1;
  private isPlaying = false;
  private currentTsMs: number;
  private readonly anchorTsMs: number;

  constructor(init: SessionInit, startTsMs: number) {
    super(init);
    this.currentTsMs = startTsMs;
    this.anchorTsMs = startTsMs;
  }

  async play(): Promise<void> {
    this.isPlaying = true;
    this.signaling.send({
      type: 'session_start',
      session_id: this.sessionId,
      device_serial: this.deviceSerial,
      channel_id: this.channelId,
      mode: 'playback',
      protocol: 'p2p',
    });
    this.emitPlaybackClock(this.currentTsMs, this.anchorTsMs, this.playbackRate, true);
  }

  async pause(): Promise<void> {
    this.isPlaying = false;
    this.emitPlaybackClock(this.currentTsMs, this.anchorTsMs, this.playbackRate, false);
  }

  async seek(targetTsMs: number): Promise<void> {
    this.currentTsMs = targetTsMs;
    this.signaling.send({
      type: 'session_start',
      session_id: this.sessionId,
      device_serial: this.deviceSerial,
      channel_id: this.channelId,
      mode: 'playback',
      protocol: 'p2p',
    });
    this.emitPlaybackClock(this.currentTsMs, this.anchorTsMs, this.playbackRate, this.isPlaying);
  }

  async setPlaybackRate(rate: number): Promise<void> {
    this.playbackRate = rate;
    this.emitPlaybackClock(this.currentTsMs, this.anchorTsMs, this.playbackRate, this.isPlaying);
  }
}
